import { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { IDecisionForm } from 'DecisionPackage/MainModule/interfaces/IDecisionForm';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { IDecisionOrganizationForm } from 'DecisionPackage/MainModule/interfaces/IDecisionOrganizationForm';
import { IKnmErknmFormJudgment } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormJudgment';
import { IKnmErknmFormJudgmentActsAndDecisions } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormJudgmentActsAndDecisions';
import { IKnmErknmFormJudgmentCanceled } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormJudgmentCanceled';
import { IKnmErknmFormJudgmentSubjectData } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormJudgmentSubjectData';
import { IDecisionResultDecisionForm } from 'DecisionPackage/MainModule/interfaces/IDecisionResultDecisionForm';

import { Row } from '../../../UIPackage/FormModule/components/Row';
import styles from './Judgment.module.scss';

interface ITemplateDecisionJudgmentModalElementProps {
  formRef: IDecisionFormRef | undefined;
  modalType: 'organization' | 'decision' | 'judgments';
  judgmentItemName?: string;
  judgmentItemSubjectDataItemName?: string;
  organizationToSelect?: IDecisionOrganizationForm;
  decision?: IDecisionResultDecisionForm;
  judgmentToSelect?: IKnmErknmFormJudgment;
}

export const DecisionJudgmentModalElement = ({
  formRef,
  modalType,
  judgmentItemName,
  judgmentItemSubjectDataItemName,
  organizationToSelect,
  judgmentToSelect,
  decision,
}: ITemplateDecisionJudgmentModalElementProps) => {
  const push = formRef?.current.mutators.push;

  // @ts-ignore
  let judgmentOrganizationList: IKnmErknmFormJudgmentSubjectData[] | undefined = formRef?.current.getFieldState(
    `${judgmentItemName}.judgmentSubjectData` as keyof IDecisionForm,
  )?.value;

  // @ts-ignore
  let judgmentDecisionList: IKnmErknmFormJudgmentActsAndDecisions[] | undefined = formRef?.current.getFieldState(
    `${judgmentItemSubjectDataItemName}.cancellationActsAndDecisions` as keyof IDecisionForm,
  )?.value;
  // @ts-ignore
  let judgmentCancellationList: IKnmErknmFormJudgmentCanceled[] | undefined = formRef?.current.getFieldState(
    `${judgmentItemName}.canceledJudgments` as keyof IDecisionForm,
  )?.value;
  const [isOrganizationActive, setIsOrganizationActive] = useState<boolean>(
    organizationToSelect
      ? organizationToSelect.guid !== undefined &&
          judgmentOrganizationList !== undefined &&
          judgmentOrganizationList?.map(x => x.organizationGuid).includes(organizationToSelect.guid)
      : false,
  );

  const [isDecisionActive, setIsDecisionActive] = useState<boolean>(
    organizationToSelect
      ? decision?.guid !== undefined &&
          decision?.guid !== null &&
          judgmentDecisionList !== undefined &&
          judgmentDecisionList?.map(x => x.decisionGuid).includes(decision.guid)
      : false,
  );

  const [isJudgmentActive, setIsJudgmentActive] = useState<boolean>(
    judgmentToSelect
      ? judgmentToSelect.guid !== undefined &&
          judgmentToSelect.guid !== null &&
          judgmentCancellationList !== undefined &&
          judgmentCancellationList?.map(x => x.guid).includes(judgmentToSelect.guid)
      : false,
  );

  const isItemActive = useCallback(() => {
    if (isOrganizationActive || isDecisionActive || isJudgmentActive) {
      return styles.TemplatesItemActive;
    }

    return styles.TemplatesItem;
  }, [isOrganizationActive, isDecisionActive, isJudgmentActive]);

  const handleSelect = useCallback(() => {
    if (modalType === 'organization') {
      return handleOrganizationSelectElement();
    }

    if (modalType === 'decision' && decision?.guid) {
      return handleDecisionSelectElement();
    }

    return handleJudgmentSelectElement();
  }, [modalType, organizationToSelect]);

  const handleOrganizationSelectElement = useCallback(() => {
    if (organizationToSelect && organizationToSelect.guid) {
      // @ts-ignore
      judgmentOrganizationList = formRef?.current.getFieldState(
        `${judgmentItemName}.judgmentSubjectData` as keyof IDecisionForm,
      )?.value;

      if (!judgmentOrganizationList?.map(x => x.organizationGuid).includes(organizationToSelect.guid) && push) {
        const newSubjectData: IKnmErknmFormJudgmentSubjectData = {
          organizationGuid: organizationToSelect.guid,
          isJudgmentAgainstOrganizations: undefined,
          isJudgmentAgainstOfficialPerson: undefined,
          judgmentAgainstOrganizations: undefined,
          judgmentAgainstOfficialPerson: undefined,
          cancellationActsAndDecisions: undefined,
          listId: uuidv4(),
          isNew: true,
        };

        push(`${judgmentItemName}.judgmentSubjectData`, newSubjectData);
        setIsOrganizationActive(true);
      }
    }
  }, [formRef, judgmentItemName, organizationToSelect, judgmentOrganizationList]);

  const handleDecisionSelectElement = useCallback(() => {
    if (decision?.guid && judgmentItemSubjectDataItemName && !isDecisionActive) {
      // @ts-ignore
      judgmentDecisionList = formRef?.current.getFieldState(
        `${judgmentItemSubjectDataItemName}.cancellationActsAndDecisions` as keyof IDecisionForm,
      )?.value;

      if (!judgmentDecisionList?.map(x => x.decisionGuid).includes(decision.guid) && push) {
        const newActCancellationInformation: IKnmErknmFormJudgmentActsAndDecisions = {
          actGuid: undefined,
          decisionGuid: decision.guid,
          title: `Решение №${decision.number}`,
          cancellationInformation: undefined,
          newCancellationInformation: undefined,
          explanation: undefined,
          newExplanation: undefined,
          isNew: true,
          listId: uuidv4(),
        };

        push(`${judgmentItemSubjectDataItemName}.cancellationActsAndDecisions`, newActCancellationInformation);
        setIsDecisionActive(true);
      }
    }
  }, [formRef, judgmentItemName, organizationToSelect, judgmentDecisionList]);

  const handleJudgmentSelectElement = useCallback(() => {
    if (judgmentToSelect && judgmentToSelect.guid && !isJudgmentActive) {
      // @ts-ignore
      judgmentCancellationList = formRef?.current.getFieldState(
        `${judgmentItemName}.canceledJudgments` as keyof IDecisionForm,
      )?.value;
      if (!judgmentCancellationList?.map(x => x.guid).includes(judgmentToSelect.guid) && push) {
        const newCanceledJudgment: IKnmErknmFormJudgmentCanceled = {
          guid: judgmentToSelect.guid,
          numberJudgment: judgmentToSelect.numberJudgment ? judgmentToSelect.numberJudgment : undefined,
          judgmentSubjectData: judgmentToSelect.judgmentSubjectData,
          listId: uuidv4(),
        };

        push(`${judgmentItemName}.canceledJudgments`, newCanceledJudgment);
        setIsJudgmentActive(true);
      }
    }
  }, [formRef, judgmentItemName, judgmentToSelect, judgmentCancellationList]);

  return (
    <div className={isItemActive()} onClick={handleSelect}>
      {modalType === 'organization' && organizationToSelect && (
        <>
          <Row>
            <div className={styles.InformationContentBlock}>
              {organizationToSelect.inn && (
                <>
                  <div className={isOrganizationActive ? styles.InformationTitleActive : styles.InformationTitle}>
                    ИНН
                  </div>
                  <div className={isOrganizationActive ? styles.InformationTextActive : styles.InformationText}>
                    {organizationToSelect.inn}
                  </div>
                </>
              )}
              {organizationToSelect.ogrn && (
                <>
                  <div className={isOrganizationActive ? styles.InformationTitleActive : styles.InformationTitle}>
                    ОГРН
                  </div>
                  <div className={isOrganizationActive ? styles.InformationTextActive : styles.InformationText}>
                    {organizationToSelect.ogrn}
                  </div>
                </>
              )}
              {organizationToSelect.kpp && (
                <>
                  <div className={isOrganizationActive ? styles.InformationTitleActive : styles.InformationTitle}>
                    КПП
                  </div>
                  <div className={isOrganizationActive ? styles.InformationTextActive : styles.InformationText}>
                    {organizationToSelect.kpp}
                  </div>
                </>
              )}
              {organizationToSelect.rafpNza && (
                <>
                  <div className={isOrganizationActive ? styles.InformationTitleActive : styles.InformationTitle}>
                    НЗА
                  </div>
                  <div className={isOrganizationActive ? styles.InformationTextActive : styles.InformationText}>
                    {organizationToSelect.rafpNza}
                  </div>
                </>
              )}
            </div>
          </Row>
          {organizationToSelect.country?.label && (
            <Row>
              <div className={styles.InformationContentBlock}>
                <div className={isOrganizationActive ? styles.InformationTitleActive : styles.InformationTitle}>
                  Государство, в котором указанное юридическое лицо зарегистрировано
                </div>
                <div className={isOrganizationActive ? styles.InformationTextActive : styles.InformationText}>
                  {organizationToSelect.country?.label}
                </div>
              </div>
            </Row>
          )}
          <Row>
            <div className={isOrganizationActive ? styles.InformationTextActive : styles.InformationText}>
              {organizationToSelect.originalName}
            </div>
          </Row>
        </>
      )}
      {modalType === 'decision' && (
        <>
          {decision && (
            <>
              <Row>
                <div className={styles.InformationContentBlock}>
                  <div className={isDecisionActive ? styles.InformationTitleActive : styles.InformationTitle}>№</div>
                  <div className={isDecisionActive ? styles.InformationTextActive : styles.InformationText}>
                    {decision.number}
                  </div>
                  <div className={isDecisionActive ? styles.InformationTitleActive : styles.InformationTitle}>
                    Дата решения
                  </div>
                  <div className={isDecisionActive ? styles.InformationTextActive : styles.InformationText}>
                    {decision.date}
                  </div>
                </div>
              </Row>
              <Row>
                <div className={isDecisionActive ? styles.InformationTextActive : styles.InformationText}>
                  {decision.kind?.name}
                </div>
              </Row>
            </>
          )}
        </>
      )}
      {modalType === 'judgments' && judgmentToSelect && (
        <>
          <Row>
            <div className={styles.InformationContentBlock}>
              <div className={isJudgmentActive ? styles.InformationTitleActive : styles.InformationTitle}>№</div>
              <div className={isJudgmentActive ? styles.InformationTextActive : styles.InformationText}>
                {judgmentToSelect.numberJudgment}
              </div>
              <div className={isJudgmentActive ? styles.InformationTitleActive : styles.InformationTitle}>
                Дата судебного решения
              </div>
              <div className={isJudgmentActive ? styles.InformationTextActive : styles.InformationText}>
                {judgmentToSelect.dateJudgment}
              </div>
            </div>
          </Row>
          <Row>
            <div className={isJudgmentActive ? styles.InformationTextActive : styles.InformationText}>
              {judgmentToSelect.judgmentAction}
            </div>
          </Row>
        </>
      )}
    </div>
  );
};
